import { Transaction, Allocation } from "@/types/payments";

/**
 * Allocate to transactions sequentially based on a sorted list
 */
function allocateSequentially(
  sortedTransactions: Transaction[],
  availableAmount: number
): Allocation[] {
  const allocations: Allocation[] = [];
  let remaining = availableAmount;

  for (const transaction of sortedTransactions) {
    if (remaining <= 0) break;

    const amount = Math.min(transaction.currentBalance, remaining);

    allocations.push({
      transactionId: transaction.id,
      transactionNumber: transaction.transactionNumber,
      allocatedAmount: amount,
      transactionBalance: transaction.currentBalance,
      isPromised: false,
    });

    remaining -= amount;
  }

  return allocations;
}

/**
 * Strategy: Oldest First
 * Allocates to transactions by date (oldest first)
 */
export function allocateOldestFirst(
  transactions: Transaction[],
  availableAmount: number
): Allocation[] {
  // Sort by transaction date ascending
  const sorted = [...transactions].sort(
    (a, b) =>
      new Date(a.transactionDate).getTime() -
      new Date(b.transactionDate).getTime()
  );

  return allocateSequentially(sorted, availableAmount);
}

/**
 * Strategy: Largest First
 * Allocates to highest balance transactions first
 */
export function allocateLargestFirst(
  transactions: Transaction[],
  availableAmount: number
): Allocation[] {
  // Sort by current balance descending
  const sorted = [...transactions].sort(
    (a, b) => b.currentBalance - a.currentBalance
  );

  return allocateSequentially(sorted, availableAmount);
}

/**
 * Strategy: Smallest First
 * Allocates to lowest balance transactions first (snowball method)
 */
export function allocateSmallestFirst(
  transactions: Transaction[],
  availableAmount: number
): Allocation[] {
  // Sort by current balance ascending
  const sorted = [...transactions].sort(
    (a, b) => a.currentBalance - b.currentBalance
  );

  return allocateSequentially(sorted, availableAmount);
}

/**
 * Calculate days overdue for a transaction
 */
function calculateDaysOverdue(dueDate: Date): number {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Strategy: Due Date Priority
 * Allocates to most overdue transactions first
 */
export function allocateDueDatePriority(
  transactions: Transaction[],
  availableAmount: number
): Allocation[] {
  // Calculate days overdue and sort by most overdue first
  const sorted = [...transactions].sort((a, b) => {
    const daysOverdueA = calculateDaysOverdue(a.dueDate);
    const daysOverdueB = calculateDaysOverdue(b.dueDate);
    return daysOverdueB - daysOverdueA;
  });

  return allocateSequentially(sorted, availableAmount);
}

/**
 * Strategy: Promise Priority
 * Allocates to promised transactions first (by priority), then remaining to non-promised
 */
export function allocatePromisePriority(
  transactions: Transaction[],
  promisedTransactions: Array<{
    transactionId: string;
    promisedAmount: number;
    priority: number;
  }>,
  availableAmount: number
): Allocation[] {
  const allocations: Allocation[] = [];
  let remaining = availableAmount;

  // Sort promised by priority
  const sortedPromised = [...promisedTransactions].sort(
    (a, b) => a.priority - b.priority
  );

  // Allocate to promised transactions
  for (const promised of sortedPromised) {
    if (remaining <= 0) break;

    const transaction = transactions.find((t) => t.id === promised.transactionId);
    if (!transaction) continue;

    const amount = Math.min(
      promised.promisedAmount,
      transaction.currentBalance,
      remaining
    );

    allocations.push({
      transactionId: transaction.id,
      transactionNumber: transaction.transactionNumber,
      allocatedAmount: amount,
      transactionBalance: transaction.currentBalance,
      isPromised: true,
      promisePriority: promised.priority,
    });

    remaining -= amount;
  }

  // Allocate remaining to non-promised transactions
  if (remaining > 0) {
    const nonPromised = transactions.filter(
      (t) => !promisedTransactions.some((p) => p.transactionId === t.id)
    );
    const remainingAllocations = allocateOldestFirst(nonPromised, remaining);
    allocations.push(...remainingAllocations);
  }

  return allocations;
}
